import { ChevronDown, ChevronRight, Folder as FolderIcon, FolderPlus, Search } from 'lucide-react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useDialogContainer } from '@/components/ui/dialog-container-context';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/NewButton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { createFolder, useFolderTree, type FolderTree } from '@/hooks/useFolders';
import { apiErrorMessage } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { flattenFolderTree, newFolderNameError } from '@/utils/document-utils';
import Loading from '../shared/Loading';

/** Identifies the matter root while an inline subfolder is being named. */
const ROOT_KEY = 'root';

/** Number of folders beyond which the tree gets a search box. */
const SEARCH_THRESHOLD = 8;

const folderValue = (folder: FolderTree, valueMode: 'id' | 'guid'): string =>
  valueMode === 'guid' ? folder.guid : folder.folder_id.toString();

interface FolderPickerProps {
  matterSlug: string;
  /** Selected folder id — or guid when `valueMode` is 'guid'. null is the matter root. */
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  valueMode?: 'id' | 'guid';
  rootLabel?: string;
  placeholder?: string;
  triggerId?: string;
  triggerClassName?: string;
  /** Show the per-row action that creates a subfolder inline. */
  allowCreateFolder?: boolean;
}

export const FolderPicker: React.FC<FolderPickerProps> = ({
  matterSlug,
  value,
  onChange,
  disabled = false,
  valueMode = 'id',
  rootLabel = '/',
  placeholder = 'Select folder',
  triggerId,
  triggerClassName,
  allowCreateFolder = false,
}) => {
  const { folderTree, isLoading, mutate } = useFolderTree(matterSlug);
  const dialogContainer = useDialogContainer();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expandedGuids, setExpandedGuids] = useState<Set<string>>(new Set());
  const [creatingUnder, setCreatingUnder] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const folderIndex = useMemo(() => {
    const index = new Map<string, { displayPath: string; parentPath: string; ancestorGuids: string[] }>();

    const walk = (nodes: FolderTree[], names: string[], ancestorGuids: string[]) => {
      for (const node of nodes) {
        const nodeNames = [...names, node.name];
        index.set(folderValue(node, valueMode), {
          displayPath: nodeNames.join(' / '),
          parentPath: names.join(' / '),
          ancestorGuids,
        });
        if (node.children.length) {
          walk(node.children, nodeNames, [...ancestorGuids, node.guid]);
        }
      }
    };

    walk(folderTree, [], []);
    return index;
  }, [folderTree, valueMode]);

  const flatFolders = useMemo(() => flattenFolderTree(folderTree), [folderTree]);

  const trimmedQuery = query.trim().toLowerCase();
  const searchResults = trimmedQuery
    ? flatFolders.filter((folder) => folder.displayName.toLowerCase().includes(trimmedQuery))
    : null;

  const triggerLabel = value === null ? rootLabel : (folderIndex.get(value)?.displayPath ?? placeholder);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setQuery('');
      setCreatingUnder(null);
      setNewFolderName('');
      // Reveal the current selection rather than making the user re-expand its parents.
      setExpandedGuids(new Set(value === null ? [] : (folderIndex.get(value)?.ancestorGuids ?? [])));
    }
  };

  const selectFolder = (next: string | null) => {
    onChange(next);
    setIsOpen(false);
  };

  const toggleExpanded = (guid: string) => {
    setExpandedGuids((previous) => {
      const next = new Set(previous);
      if (!next.delete(guid)) {
        next.add(guid);
      }
      return next;
    });
  };

  const startCreating = (parent: FolderTree | null) => {
    if (parent) {
      setExpandedGuids((previous) => new Set(previous).add(parent.guid));
    }
    setCreatingUnder(parent ? parent.guid : ROOT_KEY);
    setNewFolderName('');
  };

  const submitNewFolder = async (parent: FolderTree | null) => {
    const validationError = newFolderNameError(newFolderName, parent ?? undefined);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsCreating(true);
    try {
      const created = await createFolder(matterSlug, {
        name: newFolderName.trim(),
        parent_folder_id: parent?.folder_id,
      });
      await mutate();
      setCreatingUnder(null);
      setNewFolderName('');
      onChange(valueMode === 'guid' ? created.guid : created.folder_id.toString());
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to create folder');
    } finally {
      setIsCreating(false);
    }
  };

  const renderCreateButton = (parent: FolderTree | null) => (
    <button
      type="button"
      onClick={() => startCreating(parent)}
      aria-label={parent ? `Create subfolder in ${parent.name}` : 'Create new folder'}
      className="folder-create pointer-events-none absolute right-1 top-1/2 z-10 inline-flex -translate-y-1/2 items-center gap-1 whitespace-nowrap rounded-sm bg-white p-1 text-xs font-medium text-slate-500 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-800 focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 sm:px-1.5 sm:py-1 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100"
    >
      <FolderPlus className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{parent ? 'Create subfolder' : 'Create new folder'}</span>
    </button>
  );

  const renderCreateRow = (parent: FolderTree | null, depth: number) => (
    // Offset by the width of the expand chevron so the row lines up with its siblings.
    <div
      className="flex items-center gap-2 rounded-sm bg-accent/40 py-0.5 pr-1"
      style={{ paddingLeft: `${depth * 14 + 20}px` }}
    >
      <FolderPlus className="h-4 w-4 shrink-0 text-amber-500/40 fill-amber-400/15" />
      <Input
        // biome-ignore lint/a11y/noAutofocus: the row only exists in response to the user asking to name a folder
        autoFocus
        value={newFolderName}
        onChange={(event) => setNewFolderName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            submitNewFolder(parent);
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            // Cancel the new folder without also dismissing the picker.
            event.stopPropagation();
            setCreatingUnder(null);
          }
        }}
        onBlur={(event) => {
          // Clicking Create moves focus to the button; don't discard the name before that click lands.
          if (event.relatedTarget instanceof HTMLElement && event.currentTarget.parentElement?.contains(event.relatedTarget)) {
            return;
          }
          if (!newFolderName.trim() && !isCreating) {
            setCreatingUnder(null);
          }
        }}
        placeholder="Folder name"
        aria-label={parent ? `Name of new subfolder in ${parent.name}` : 'Name of new folder'}
        disabled={isCreating}
        className="h-7 rounded-none border-0 bg-transparent px-0 py-0 text-sm font-medium"
      />
      <Button
        type="button"
        size="xs"
        text="Create"
        loading={isCreating}
        disabled={!newFolderName.trim() || isCreating}
        onClick={() => submitNewFolder(parent)}
      />
    </div>
  );

  const renderFolderNode = (folder: FolderTree, depth: number): React.ReactNode => {
    const nodeValue = folderValue(folder, valueMode);
    const hasChildren = folder.children.length > 0;
    const isExpanded = expandedGuids.has(folder.guid);

    return (
      <div key={folder.guid}>
        <div
          className={cn(
            'group relative flex w-full min-w-0 items-center rounded-sm pr-1 hover:bg-accent/50',
            allowCreateFolder && 'sm:hover:pr-40 sm:has-[.folder-create:focus-visible]:pr-40 sm:[@media(hover:none)]:pr-40',
            value === nodeValue && 'bg-primary-50',
          )}
          style={{ paddingLeft: `${depth * 14}px` }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggleExpanded(folder.guid)}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${folder.name}`}
              className="shrink-0 rounded-sm p-0.5 text-slate-500 hover:bg-slate-200"
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <span className="w-5 shrink-0" />
          )}
          <button
            type="button"
            onClick={() => selectFolder(nodeValue)}
            title={folder.name}
            className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left text-sm"
          >
            <FolderIcon className="h-4 w-4 shrink-0 text-amber-500/40 fill-amber-400/15" />
            <span className="truncate font-medium">{folder.name}</span>
          </button>
          {allowCreateFolder && renderCreateButton(folder)}
        </div>

        {creatingUnder === folder.guid && renderCreateRow(folder, depth + 1)}

        {isExpanded && folder.children.map((child) => renderFolderNode(child, depth + 1))}
      </div>
    );
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={triggerId}
          disabled={disabled || isLoading}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm ring-offset-white focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            triggerClassName,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {value !== null && <FolderIcon className="h-4 w-4 shrink-0 text-amber-500/40 fill-amber-400/15" />}
            <span className="truncate">{triggerLabel}</span>
          </span>
          {isLoading ? <Loading /> : <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />}
        </button>
      </PopoverTrigger>

      <PopoverContent
        container={dialogContainer}
        align="start"
        className="flex max-h-(--radix-popover-content-available-height) w-(--radix-popover-trigger-width) min-w-72 flex-col p-0"
      >
        {flatFolders.length > SEARCH_THRESHOLD && (
          <div className="shrink-0 border-b border-slate-100 p-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search folders"
                aria-label="Search folders"
                className="h-8 pl-7 text-sm"
              />
            </div>
          </div>
        )}

        <div className="max-h-[28rem] min-h-0 flex-1 overflow-y-auto p-1">
          {searchResults ? (
            searchResults.length > 0 ? (
              searchResults.map((folder) => {
                const resultValue = folderValue(folder.original, valueMode);
                const parentPath = folderIndex.get(resultValue)?.parentPath;

                return (
                  <button
                    key={folder.guid}
                    type="button"
                    onClick={() => selectFolder(resultValue)}
                    className={cn(
                      'flex w-full min-w-0 items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent/50',
                      value === resultValue && 'bg-primary-50',
                    )}
                  >
                    <FolderIcon className="h-4 w-4 shrink-0 text-amber-500/40 fill-amber-400/15" />
                    <span className="truncate font-medium">{folder.displayName}</span>
                    {parentPath ? (
                      <span className="ml-auto shrink truncate text-xs text-slate-400">in {parentPath}</span>
                    ) : null}
                  </button>
                );
              })
            ) : (
              <p className="px-2 py-6 text-center text-sm text-slate-500">No folders match "{query.trim()}"</p>
            )
          ) : (
            <>
              <div className={cn('group relative flex w-full min-w-0 items-center rounded-sm pr-1 hover:bg-accent/50', allowCreateFolder && 'sm:hover:pr-40 sm:has-[.folder-create:focus-visible]:pr-40 sm:[@media(hover:none)]:pr-40', value === null && 'bg-primary-50')}>
                <span className="w-5 shrink-0" />
                <button
                  type="button"
                  onClick={() => selectFolder(null)}
                  className="flex min-w-0 flex-1 items-center py-1.5 text-left text-sm"
                >
                  <span className="truncate font-medium">{rootLabel}</span>
                </button>
                {allowCreateFolder && renderCreateButton(null)}
              </div>

              {creatingUnder === ROOT_KEY && renderCreateRow(null, 1)}

              {folderTree.map((folder) => renderFolderNode(folder, 1))}

              {flatFolders.length === 0 && !isLoading && (
                <p className="px-2 py-6 text-center text-sm text-slate-500">
                  No folders yet{allowCreateFolder ? '. Choose Create new folder above.' : ''}
                </p>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
